<script setup>
import { ref, computed, onMounted } from 'vue';
import emojiGroups from 'shared/components/emoji/emojisGroup.json';

const emit = defineEmits(['select']);

const searchQuery = ref('');
const allEmojis = ref([]);

// Load and flatten all emojis
onMounted(() => {
  allEmojis.value = emojiGroups.flatMap(group =>
    group.emojis.map(emoji => ({
      emoji: emoji.emoji,
      name: emoji.name,
      slug: emoji.slug,
      searchString: `${emoji.name.toLowerCase()} ${emoji.slug.toLowerCase()}`,
    }))
  );
});

// Filter emojis based on search
const filteredEmojis = computed(() => {
  if (!searchQuery.value) {
    return allEmojis.value;
  }

  const query = searchQuery.value.toLowerCase();
  return allEmojis.value.filter(emoji => emoji.searchString.includes(query));
});

const handleEmojiClick = emoji => {
  emit('select', emoji.emoji);
};
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Search Bar -->
    <div class="px-3 pt-3 pb-2 border-b border-n-weak flex-shrink-0">
      <div class="relative">
        <span
          class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-n-slate-11 pointer-events-none"
        />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search emojis..."
          class="w-full pl-8 pr-3 py-2 text-sm bg-n-solid-2 border border-n-weak rounded-lg text-n-slate-12 placeholder:text-n-slate-11 focus:outline-none focus:ring-2 focus:ring-n-blue-5"
        />
      </div>
    </div>

    <!-- Emoji Grid -->
    <div class="flex-1 overflow-y-auto p-3">
      <div
        v-if="filteredEmojis.length > 0"
        class="grid grid-cols-6 sm:grid-cols-8 gap-1"
      >
        <button
          v-for="emoji in filteredEmojis"
          :key="emoji.slug"
          type="button"
          :title="emoji.name"
          class="flex items-center justify-center aspect-square text-xl hover:bg-n-solid-3 rounded transition-colors"
          @click="handleEmojiClick(emoji)"
        >
          {{ emoji.emoji }}
        </button>
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center py-8 text-n-slate-11"
      >
        <span class="i-lucide-search size-8 mb-2 opacity-50" />
        <p class="text-sm">No emojis found</p>
      </div>
    </div>

    <!-- Info Footer -->
    <div class="px-3 py-2 border-t border-n-weak bg-n-solid-2">
      <p class="text-xs text-n-slate-11 text-center">
        {{ filteredEmojis.length }} emoji{{
          filteredEmojis.length !== 1 ? 's' : ''
        }}
      </p>
    </div>
  </div>
</template>
